import type { BarSeriesOption } from 'echarts/charts';
import type { DatasetOption } from 'echarts/types/dist/shared';

import type { I18nService } from '@core/i18n-legacy';
import { DatasetBase } from '@core/reports-types';
import { datasetChartLabelFormatterOptions } from '@core/reports-util';
import { valueFormatter } from '@core/utils-legacy';

import { BAR_CHART_BAR_MAX_WIDTH } from '../chart-layout';

export interface RatioBars {
  dataset: DatasetOption;
  series: BarSeriesOption[];
}

/**
 * Single bar series for a single-dimension ratio metric.
 *
 * The API sends `null` as the ratio whenever the denominator is 0, which is not
 * the same as a `0:1` one. echarts skips a `null` cell entirely - no bar, no
 * label, and the category ends up looking like it was never in the data - so
 * those rows are drawn as a zero-length bar labelled with the empty marker
 * instead, the same way a `null` rate is handled in `buildSignSplitBars`.
 *
 * Labels read the value out of the original dataset by row index rather than
 * off the substituted source, so an empty row stays distinguishable from a
 * genuine `0:1` one.
 */
export const buildRatioBars = (dataset: DatasetBase, i18n: I18nService): RatioBars => {
  const [, valueDimension] = dataset.dataset.dimensions;
  const values = dataset.dataset.source.map(([, value]) =>
    typeof value === 'number' ? value : null,
  );

  const label = () => ({
    show: true,
    position: <const>'right',
    formatter: ({ dataIndex }: { dataIndex: number }) =>
      valueFormatter(values[dataIndex], datasetChartLabelFormatterOptions(dataset), i18n),
    rich: {},
  });

  return {
    dataset: <DatasetOption>{
      ...dataset.dataset,
      source: dataset.dataset.source.map(([category], index) => [category, values[index] ?? 0]),
    },
    series: [
      {
        id: valueDimension.id || valueDimension.name,
        name: valueDimension.name,
        type: 'bar',
        seriesLayoutBy: 'column',
        cursor: 'default',
        barMaxWidth: BAR_CHART_BAR_MAX_WIDTH,
        label: label(),
        emphasis: { label: label() },
      },
    ],
  };
};
